import { Field, Form, Formik, useFormikContext } from 'formik'
import type { FormikConfig } from 'formik'
import type { ReactElement } from 'react'
import { fromJS } from 'immutable'
import { useEffect, useId, useState } from 'react'
import { useSelector } from 'react-redux'
import cc from 'classcat'

import { FormikColorField as ColorField } from '../../../../formComponents/ColorField'
import { FormikOnChange } from '../product/ProductSettings'
import { Popover } from '../../../../Popover'
import { emptyIconPluginData } from '../../../../editorSidebarBlockTemplates'
import { themeGroupDelimiter } from '../../../../../../utils/themes'
import { useColorOptions } from '../../../../../utils/hooks/colorSettings'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import FormGroup from '../../../../formComponents/FormikGroup'
import FormikRangeInputField from '../../../../formComponents/FormikRangeInputField'
import FormikValue from '../../../../formComponents/FormikValue'
import IconPicker from '../../../../formComponents/IconPicker/IconPicker'
import LinkTooltipSettingsLayer from '../components/LinkTooltip/LinkTooltipSettingsLayer'
import RadioButtonGroup from '../../../../formComponents/FormikRadioButtonField'

export type IconPluginData = {
  type: string
  link: string
  opentab: boolean
  alignment: 'left' | 'center' | 'right'
  spacing: number
  sizing: number
  color: string
  backgroundColor: string
  borderColor: string
  borderSize: number
  backgroundRadius: number
  backgroundSize: number
}

type Props = Readonly<Omit<WorkspacePluginSettingsProps, 'data'> & { data: IconPluginData }>

type SettingsTabProps = Readonly<{
  id: string
  initialData?: IconPluginData
  colorOptions: ReturnType<typeof useColorOptions>
}>

export default function IconSettings({
  data: formData,
  onCancel,
  onDataChange,
  onSave,
  renderLayout,
}: Props): ReactElement {
  const id = useId()
  const [activeTab, setActiveTab] = useState<'icon-main' | 'icon-advanced'>('icon-main')
  const t = useTranslate('interface')

  const firstTabTitle = formData.type
    ? t('components.iconElementComponent.settingsModal.firstTab.title.onEdit')
    : t('components.iconElementComponent.settingsModal.firstTab.title.onCreate')
  const secondTabTitle = t('components.iconElementComponent.settingsModal.secondTab.title')
  const title = activeTab === 'icon-main' ? firstTabTitle : secondTabTitle

  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name'], '').replace('.', '_'))
  const [themeGroupName] = themeName.split(themeGroupDelimiter)
  const tThemeSettings = (subKey: string) => t(`themes:${themeGroupName}.settings.${subKey}`)
  const tColorOptions = (subKey: string) => t(`components.colorFieldComponent.popover.colorPresets.${subKey}.tooltip`)
  const colorOptions = useColorOptions(tColorOptions, tThemeSettings)

  const body =
    activeTab === 'icon-main' ? (
      <MainSettings colorOptions={colorOptions} id={id} />
    ) : (
      <AdvancedSettings colorOptions={colorOptions} id={id} />
    )

  const tabs = [
    {
      name: 'icon-main',
      title: firstTabTitle,
      active: activeTab === 'icon-main',
      onClick: () => setActiveTab('icon-main'),
    },
    {
      name: 'icon-advanced',
      title: secondTabTitle,
      active: activeTab === 'icon-advanced',
      onClick: () => setActiveTab('icon-advanced'),
    },
  ]

  const footer = (
    <ButtonRow>
      <Button variant="cancel" type="button" onClick={onCancel}>
        {t(`components.iconElementComponent.settingsModal.cancelButton.label`)}
      </Button>
      <Button type="submit">{t(`components.iconElementComponent.settingsModal.saveButton.label`)}</Button>
    </ButtonRow>
  )

  const handleSubmit: FormikConfig<IconPluginData>['onSubmit'] = (values) => {
    return onSave(fromJS(values))
  }
  const handleChange = (values: IconPluginData) => onDataChange(fromJS(values))

  return (
    <Formik<IconPluginData> initialValues={formData} onSubmit={handleSubmit}>
      <Form translate="no">
        <FormikOnChange<IconPluginData> onChange={handleChange} />
        {renderLayout({ title, body, tabs, footer })}
      </Form>
    </Formik>
  )
}

function MainSettings({ colorOptions, id }: SettingsTabProps) {
  const { values, resetForm } = useFormikContext<IconPluginData>()
  const { alignment, spacing, sizing, color } = emptyIconPluginData
  const [showIconPicker, setShowIconPicker] = useState(false)
  const t = useTranslate('interface', 'components.iconElementComponent.settingsModal.firstTab')

  const areSettingsDisabled = !values.type
  const isOpenTabDisabled = !values.link.trim()

  useEffect(() => {
    if (!values.type) {
      // Delay showing the icon picker to prevent positioning flicker
      const timer = setTimeout(() => {
        setShowIconPicker(true)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [values.type])

  return (
    <>
      <FormikValue name="type">
        {(icon, setIcon) => (
          <div className="ep-form-row ep-form-row-min-margin">
            <Popover
              open={showIconPicker}
              onOpenChange={setShowIconPicker}
              className="ep-icon-picker"
              side="left"
              align="center"
              sideOffset={35}
              avoidCollisions
              anchor={
                icon ? (
                  <div className="dali-plugin-icon-default body">
                    <span
                      className={cc([
                        {
                          [`ep-${icon}-icon`]: icon,
                        },
                      ])}
                    />
                  </div>
                ) : (
                  <div className="dali-plugin-icon-default">
                    <span className="dali-plugin-icon-placeholder" />
                    <p className="dali-plugin-icon-info">{t('emptyStateText')}</p>
                  </div>
                )
              }
              trigger={
                <button type="button" className="dali-plugin-icon-button-change">
                  {t(values.type ? 'changeIconLink.label' : 'selectIconLink.label')}
                </button>
              }
            >
              <IconPicker
                icon={icon}
                setIcon={(newIcon) => {
                  if (!icon) setShowIconPicker(true)
                  setIcon(newIcon)
                }}
              />
            </Popover>
          </div>
        )}
      </FormikValue>
      <FormikValue name="link">
        {(link, setLink) => (
          <LinkTooltipSettingsLayer
            t={t}
            link={link}
            setLink={setLink}
            label={'linkInputField.label'}
            disabled={areSettingsDisabled}
          />
        )}
      </FormikValue>
      <FormGroup
        tagName="label"
        labelAfterComponent
        label={t('openLinkInNewTabCheckbox.label')}
        className={cc(['ep-form-row ep-form-row-no-margin', { disabled: isOpenTabDisabled }])}
        name="opentab"
      >
        <Field name="opentab" className="ep-form-row-checkbox" type="checkbox" disabled={isOpenTabDisabled} />
      </FormGroup>
      <div className={cc(['dali-plugin-icon-settings', { disabled: areSettingsDisabled }])}>
        {/* nesting required in order to override an overly specific rule
          meant to unstyle the top level fieldset in the bottom bar settings layers */}
        <fieldset disabled={areSettingsDisabled}>
          <legend>{t('styleSettingsSection.label')}</legend>
          <FormGroup className="ep-form-row" name="color" label={t('styleSettingsSection.color')}>
            <ColorField
              withHexInput
              name="color"
              presetColors={colorOptions.toJS()}
              offsetDistance={22}
              resetValue={emptyIconPluginData.color}
              side="left"
              align="end"
            />
          </FormGroup>
          <FormGroup className="ep-form-row" name="alignment" label={t('styleSettingsSection.alignmentSetting.label')}>
            <div className="dali-plugin-icon-settings-alignment">
              <RadioButtonGroup
                disabled={areSettingsDisabled}
                name="alignment"
                options={[
                  { value: 'left', label: t('styleSettingsSection.alignmentSetting.leftRadioButton.label') },
                  { value: 'center', label: t('styleSettingsSection.alignmentSetting.centerRadioButton.label') },
                  { value: 'right', label: t('styleSettingsSection.alignmentSetting.rightRadioButton.label') },
                ]}
              />
            </div>
          </FormGroup>
          <FormGroup
            label={t('styleSettingsSection.spacing')}
            className="ep-form-row"
            id={`${id}-spacing`}
            name="spacing"
          >
            <FormikRangeInputField aria-labelledby={`${id}-spacing`} name="spacing" min={0} max={50} />
          </FormGroup>
          <FormGroup className="ep-form-row" id={`${id}-sizing`} name="sizing" label={t('styleSettingsSection.sizing')}>
            <FormikRangeInputField aria-labelledby={`${id}-sizing`} name="sizing" min={15} max={100} />
          </FormGroup>
        </fieldset>
        <button
          type="button"
          className="dali-plugin-icon-reset-button"
          disabled={areSettingsDisabled}
          onClick={() =>
            resetForm({
              values: { ...values, alignment, color, spacing, sizing },
            })
          }
        >
          {t('styleSettingsSection.resetLink.label')}
        </button>
      </div>
    </>
  )
}

function AdvancedSettings({ colorOptions, id }: SettingsTabProps) {
  const { values, resetForm } = useFormikContext<IconPluginData>()

  const t = useTranslate('interface', 'components.iconElementComponent.settingsModal.secondTab')

  const { backgroundSize, backgroundRadius, backgroundColor, borderSize, borderColor } = emptyIconPluginData
  const areSettingsDisabled = !values.type

  return (
    <div className={cc(['dali-plugin-icon-settings advanced', { disabled: areSettingsDisabled }])}>
      {/* nesting required in order to override an overly specific rule
          meant to unstyle the top level fieldset in the bottom bar settings layers */}
      <fieldset disabled={areSettingsDisabled}>
        <legend>{t('backgroundSettingsSection.label')}</legend>
        <FormGroup
          className="ep-form-row icon-color"
          name="backgroundColor"
          label={t('backgroundSettingsSection.color')}
        >
          <ColorField
            withHexInput
            name="backgroundColor"
            className={cc(['icon-color', { transparent: values.backgroundColor === 'transparent' }])}
            presetColors={colorOptions.toJS()}
            offsetDistance={22}
            resetValue={emptyIconPluginData.backgroundColor}
            side="left"
            align="end"
          />
        </FormGroup>
        <FormGroup
          className="ep-form-row"
          id={`${id}-background-size`}
          name="backgroundSize"
          label={t('backgroundSettingsSection.size')}
        >
          <FormikRangeInputField aria-labelledby={`${id}-background-size`} name="backgroundSize" min={0} max={100} />
        </FormGroup>
        <FormGroup
          className="ep-form-row"
          label={t('backgroundSettingsSection.radius')}
          id={`${id}-background-radius`}
          name="backgroundRadius"
        >
          <FormikRangeInputField aria-labelledby={`${id}-background-radius`} name="backgroundRadius" min={0} max={50} />
        </FormGroup>
      </fieldset>
      <button
        type="button"
        className="dali-plugin-icon-reset-button"
        disabled={areSettingsDisabled}
        onClick={() =>
          resetForm({
            values: { ...values, backgroundSize, backgroundRadius, backgroundColor },
          })
        }
      >
        {t('backgroundSettingsSection.resetLink.label')}
      </button>
      <fieldset disabled={areSettingsDisabled}>
        <legend>{t('borderSettingsSection.label')}</legend>
        <FormGroup className="ep-form-row icon-color" name="borderColor" label={t('borderSettingsSection.color')}>
          <ColorField
            withHexInput
            name="borderColor"
            className="icon-color"
            presetColors={colorOptions.toJS()}
            offsetDistance={22}
            resetValue={emptyIconPluginData.borderColor}
            side="left"
            align="end"
          />
        </FormGroup>
        <FormGroup
          className="ep-form-row"
          label={t('borderSettingsSection.size')}
          id={`${id}-border-size`}
          name="borderSize"
        >
          <FormikRangeInputField aria-labelledby={`${id}-border-size`} name="borderSize" min={0} max={20} />
        </FormGroup>
      </fieldset>
      <button
        type="button"
        className="dali-plugin-icon-reset-button"
        disabled={areSettingsDisabled}
        onClick={() =>
          resetForm({
            values: { ...values, borderSize, borderColor },
          })
        }
      >
        {t('borderSettingsSection.resetLink.label')}
      </button>
    </div>
  )
}
